import { useState, useRef, useEffect } from "react";
import type { LangCode } from "../data/types.js";
import { LANGS } from "../i18n/strings.js";

const PILL_STYLE = {
  background: "rgba(255,255,255,0.88)",
  backdropFilter: "blur(16px) saturate(1.4)",
  WebkitBackdropFilter: "blur(16px) saturate(1.4)",
  boxShadow: "0 2px 12px rgba(26,25,23,0.08)",
} as const;

interface Props {
  lang: LangCode;
  onChange: (lang: LangCode) => void;
}

export function LangSwitcher({ lang, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  return (
    <div
      className="fixed top-0 right-0 z-30 pointer-events-none"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div
        className="mr-4 mt-3 flex justify-end pointer-events-auto"
        ref={ref}
      >
        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="inline-flex items-center gap-1 px-2.5 py-2 rounded-2xl text-[12px] font-medium text-ink-1"
            style={PILL_STYLE}
          >
            <span className="text-sm">{current.flag}</span>
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform ${open ? "rotate-180" : ""}`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {open && (
            <div
              className="absolute top-full right-0 mt-1.5 rounded-2xl py-1.5 min-w-[140px]"
              style={PILL_STYLE}
            >
              {LANGS.map((opt) => (
                <button
                  key={opt.code}
                  onClick={() => {
                    onChange(opt.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] font-medium transition-all ${
                    lang === opt.code
                      ? "text-kra"
                      : "text-ink-1 active:bg-surface-1"
                  }`}
                >
                  <span className="text-sm">{opt.flag}</span>
                  <span>{opt.label}</span>
                  {lang === opt.code && (
                    <span className="ml-auto text-kra text-sm">✓</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
